import React, { useState, useEffect } from "react";
import GameStateProvider from "./GameStateProvider";
import AudioProvider from "./AudioProvider";
import TutorialProvider from "./TutorialProvider";
import GameContainer from "./GameContainer";
import ErrorBoundary from "./components/ErrorBoundary";
import OrientationLock from "./components/OrientationLock";
import { motion } from "framer-motion";
import { ASSETS } from "./assetManifest.js";
import { CONSTANTS } from "./data.js";

const App: React.FC = () => {
  const [showSplash, setShowSplash] = useState(true);
  const [isFadingOut, setIsFadingOut] = useState(false);

  const splash = CONSTANTS.SPLASH;

  useEffect(() => {
    if (!splash) {
      setShowSplash(false);
      return;
    }

    const fadeOutTimer = setTimeout(() => {
      setIsFadingOut(true);
    }, splash.fadeOutDelayMs);

    const hideSplashTimer = setTimeout(() => {
      setShowSplash(false);
    }, splash.fadeOutDelayMs + splash.fadeOutDurationMs);

    return () => {
      clearTimeout(fadeOutTimer);
      clearTimeout(hideSplashTimer);
    };
  }, [splash]);

  if (showSplash && splash) {
    const category = splash.imageCategory;
    const key = splash.imageKey;
    const asset =
      (ASSETS && ASSETS[category] && ASSETS[category][key]) || {
        path: "assets/default.png",
        width: 256,
        height: 256,
      };

    const width = splash.imageDisplayWidth || asset.width || 256;
    const height = splash.imageDisplayHeight || asset.height || 256;

    const bgColor = splash.backgroundColor || "#000000";
    const textColor = splash.textColor || "#ffffff";

    const gridColor = splash.gridColor;
    const gridDotSize = splash.gridDotSize;
    const gridSpacing = splash.gridSpacing;

    const handleImageClick = () => {
      window.open("https://graph.plus", "_blank");
    };

    return (
      <div
        className="h-[100dvh] w-screen overflow-hidden flex items-center justify-center"
        style={{
          backgroundColor: bgColor,
          color: textColor,
          backgroundImage: `radial-gradient(circle, ${gridColor} ${gridDotSize}px, transparent ${gridDotSize}px)`,
          backgroundSize: `${gridSpacing}px ${gridSpacing}px`,
          backgroundPosition: "50% 50%",
        }}
      >
        <div className="flex flex-col items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{
              opacity: isFadingOut ? 0 : 1,
              y: 0,
              scale: isFadingOut ? splash.fadeOutScaleTo : 1,
            }}
            transition={{
              duration: isFadingOut
                ? splash.fadeOutDurationMs / 1000
                : 0.4,
              ease: "easeOut",
            }}
          >
            <p className="uppercase tracking-widest text-sm opacity-80">
              {splash.text}
            </p>
          </motion.div>

          <motion.img
            src={asset.path}
            alt="Splash Logo"
            width={width}
            height={height}
            className="mt-3 drop-shadow-xl cursor-pointer"
            initial={{ scale: splash.popScaleFrom }}
            animate={{
              scale: isFadingOut
                ? splash.fadeOutScaleTo
                : [splash.popScaleFrom, splash.popOvershoot, splash.popScaleTo],
              opacity: isFadingOut ? 0 : 1,
            }}
            transition={{
              duration: isFadingOut
                ? splash.fadeOutDurationMs / 1000
                : splash.popDurationMs / 1000,
              times: isFadingOut ? undefined : [0, 0.6, 1],
              ease: "easeOut",
            }}
            onClick={handleImageClick}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] w-screen overflow-hidden">
      <OrientationLock>
        <ErrorBoundary>
          <GameStateProvider>
            <AudioProvider>
              <TutorialProvider>
                <GameContainer />
              </TutorialProvider>
            </AudioProvider>
          </GameStateProvider>
        </ErrorBoundary>
      </OrientationLock>
    </div>
  );
};

export default App;
